import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { Home, Shield, BarChart3, DollarSign, Users, AlertTriangle, CheckCircle, Activity, Bell, User, Wallet, FileText } from "lucide-react";
import { WalletManager } from "@/components/admin/WalletManager";
import { BackButton } from "@/components/BackButton";

const AdminDashboard = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="glass-card border-b border-border/50 sticky top-0 z-50 backdrop-blur-xl">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold gradient-digital bg-clip-text text-transparent">
              HiStage Admin
            </Link>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon">
                <Bell className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="sm">
                <Shield className="h-4 w-4 mr-2" />
                Moderation
              </Button>
              <Button variant="secondary" size="sm" className="shadow-lg">
                <BarChart3 className="h-4 w-4 mr-2" />
                Reports
              </Button>
              <Button variant="ghost" size="icon">
                <User className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8 flex-1">
        <BackButton to="/" />
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-3">
            <span className="gradient-digital bg-clip-text text-transparent">Platform Control Center</span>
          </h1>
          <p className="text-lg text-muted-foreground">Monitor platform health, moderate content, and manage operations</p>
        </div>

        {/* Platform Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {[
            { 
              icon: Users, 
              label: "Active Users", 
              value: "24,891", 
              change: "+1,234 today",
              gradient: "gradient-digital", 
              glow: "glow-secondary",
              positive: true
            },
            { 
              icon: Activity, 
              label: "Live Streams", 
              value: "342", 
              change: "Right now",
              gradient: "gradient-stage", 
              glow: "glow-primary",
              positive: null
            },
            { 
              icon: DollarSign, 
              label: "Daily Revenue", 
              value: "$42.5k", 
              change: "+12% vs yesterday",
              gradient: "gradient-spotlight", 
              glow: "glow-accent",
              positive: true
            },
            { 
              icon: AlertTriangle, 
              label: "Pending Reports", 
              value: "23", 
              change: "Requires attention",
              gradient: "bg-destructive/20", 
              glow: "",
              positive: false
            }
          ].map((stat, i) => (
            <div key={i} className="glass-card p-6 rounded-2xl border border-border/50 hover:scale-105 transition-transform">
              <div className={`h-14 w-14 rounded-xl ${stat.gradient} ${stat.glow} flex items-center justify-center mb-4`}>
                <stat.icon className={`h-7 w-7 ${stat.gradient === 'bg-destructive/20' ? 'text-destructive' : 'text-white'}`} />
              </div>
              <p className="text-sm text-muted-foreground mb-1">{stat.label}</p>
              <p className="text-3xl font-bold mb-2">{stat.value}</p>
              <p className={`text-xs ${
                stat.positive === true ? 'text-green-500' : 
                stat.positive === false ? 'text-destructive' : 
                'text-muted-foreground'
              }`}>
                {stat.change}
              </p>
            </div>
          ))}
        </div>

        {/* Management Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {/* Wallet Management */}
          <div className="glass-card p-8 rounded-2xl border border-border/50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Wallet className="h-6 w-6 text-accent" />
                Wallet Management
              </h2>
            </div>
            <WalletManager />
          </div>

          {/* Blog Management Card */}
          <div 
            className="glass-card p-8 rounded-2xl border border-border/50 hover:scale-105 transition-transform cursor-pointer"
            onClick={() => navigate('/admin/blog')}
          >
            <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
              <div className="h-20 w-20 rounded-2xl gradient-digital flex items-center justify-center shadow-lg">
                <FileText className="h-10 w-10 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-2">Blog Management</h2>
                <p className="text-muted-foreground">Create, edit, and manage blog posts</p>
              </div>
              <Button variant="secondary" className="shadow-md">
                Manage Blog Posts
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-10">
          {/* Moderation Queue */}
          <div className="glass-card p-8 rounded-2xl border border-border/50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Shield className="h-6 w-6 text-destructive" />
                Moderation Queue
              </h2>
              <Button variant="outline" size="sm">View All</Button>
            </div>
            <div className="space-y-4">
              {[
                { type: "Stream Report", user: "user_789", reason: "Inappropriate content", severity: "high" },
                { type: "Chat Report", user: "user_456", reason: "Spam messages", severity: "medium" },
                { type: "User Report", user: "user_123", reason: "Harassment", severity: "high" },
                { type: "Stream Report", user: "user_321", reason: "Copyright violation", severity: "medium" },
              ].map((report, i) => (
                <div key={i} className="p-5 bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border/50 hover:border-border transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <p className="font-bold text-base mb-1">{report.type}</p>
                      <p className="text-sm text-muted-foreground">{report.user}</p>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
                      report.severity === 'high' 
                        ? 'bg-destructive/20 text-destructive border border-destructive/30' 
                        : 'bg-accent/20 text-accent border border-accent/30'
                    }`}>
                      {report.severity.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm mb-4 text-foreground/80">{report.reason}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1 bg-background/50">
                      Review
                    </Button>
                    <Button variant="destructive" size="sm" className="shadow-md">
                      Take Action
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="glass-card p-8 rounded-2xl border border-border/50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Activity className="h-6 w-6 text-primary" />
                Platform Activity
              </h2>
              <Button variant="outline" size="sm">Refresh</Button>
            </div>
            <div className="space-y-3">
              {[
                { action: "New creator joined", time: "2 min ago", icon: Users, color: "text-green-500", bgColor: "bg-green-500/10" },
                { action: "Payout processed", time: "5 min ago", icon: DollarSign, color: "text-accent", bgColor: "bg-accent/10" },
                { action: "Stream ended", time: "12 min ago", icon: BarChart3, color: "text-blue-500", bgColor: "bg-blue-500/10" },
                { action: "Report resolved", time: "20 min ago", icon: CheckCircle, color: "text-green-500", bgColor: "bg-green-500/10" },
                { action: "New stream started", time: "25 min ago", icon: Activity, color: "text-primary", bgColor: "bg-primary/10" },
                { action: "User banned", time: "45 min ago", icon: Shield, color: "text-destructive", bgColor: "bg-destructive/10" },
              ].map((activity, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl hover:bg-muted/50 transition-colors border border-border/30">
                  <div className={`h-10 w-10 rounded-lg ${activity.bgColor} flex items-center justify-center flex-shrink-0`}>
                    <activity.icon className={`h-5 w-5 ${activity.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{activity.action}</p>
                    <p className="text-xs text-muted-foreground">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Payout Management */}
        <div className="glass-card p-8 rounded-2xl border border-border/50 mb-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <DollarSign className="h-6 w-6 text-accent" />
              Pending Payouts
            </h2>
            <Button variant="secondary" size="sm" className="shadow-md">Process All</Button>
          </div>
          <div className="space-y-4">
            {[
              { creator: "Creator_A", amount: 450, streams: 12 },
              { creator: "Creator_B", amount: 890, streams: 24 },
              { creator: "Creator_C", amount: 320, streams: 8 },
            ].map((payout, i) => (
              <div key={i} className="flex items-center justify-between p-5 bg-gradient-to-r from-accent/5 to-accent/10 rounded-xl border border-accent/20 hover:border-accent/40 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-full gradient-spotlight flex items-center justify-center font-bold text-lg shadow-lg">
                    {payout.creator.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold text-lg">{payout.creator}</p>
                    <p className="text-sm text-muted-foreground">{payout.streams} streams this month</p>
                  </div>
                </div>
                <div className="flex items-center gap-5">
                  <p className="text-3xl font-bold gradient-spotlight bg-clip-text text-transparent">${payout.amount}</p>
                  <Button variant="secondary" size="sm" className="shadow-md">
                    Process
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center pt-8 border-t border-border/50">
          <Link to="/">
            <Button variant="ghost" size="lg">
              <Home className="h-5 w-5 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
